import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import LoginPanel from './LoginPanel.js'
import { userSession } from '../utils/userSession.js';
import { useData } from '../context/DataContext.js';
import { Menu } from '@headlessui/react';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function NavBar({ onNavigate, currentPage, onOpenDashboard }) {
  const [user, setUser] = useState(null);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const { countries, selectedCountry, setSelectedCountry } = useData();

  // Subscribe to userSession changes
  useEffect(() => {
    const unsubscribe = userSession.subscribe((userData) => {
      if (userData) {
        // Generate initials from first and last name
        const initials = `${userData.firstName?.[0] || ''}${userData.lastName?.[0] || ''}`.toUpperCase();
        setUser(prevUser => {
          if (!prevUser || prevUser.userId !== userData.userId) {
            return { ...userData, initials };
          }
          return prevUser;
        });
      } else {
        setUser(null);
      }
    });

    // Load initial user state
    const currentUser = userSession.getUser();
    if (currentUser) {
      const initials = `${currentUser.firstName?.[0] || ''}${currentUser.lastName?.[0] || ''}`.toUpperCase();
      setUser({ ...currentUser, initials });
    }

    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    userSession.clearUser();
    setUser(null);
  };

  const navigation = [
    { name: 'Home', href: '/', current: currentPage === 'landing', onClick: () => onNavigate('landing') },
    { name: 'Events', href: '/events', current: currentPage === 'events', onClick: () => onNavigate('events') },
    { name: 'Promoters', href: '/promoters', current: currentPage === 'promoters', onClick: () => onNavigate('promoters') },
    { name: 'Partners', href: '/partners', current: currentPage === 'partners', onClick: () => onNavigate('partners') },
    { name: 'API', href: '/api', current: currentPage === 'api', onClick: () => onNavigate('api') }
  ]

  const getInitials = (user) => {
    if (!user) return '';
    return `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  };

  const initials = getInitials(user);

  return (
    <Disclosure as="nav" className="fixed w-full top-0 z-[100] bg-slate-900" style={{ position: 'fixed', zIndex: 100 }}>
      {({ close: closeMenu, open }) => (
        <>
          <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div className="relative flex h-16 items-center justify-between">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon aria-hidden="true" className="block size-6 group-data-[open]:hidden" />
                  <XMarkIcon aria-hidden="true" className="hidden size-6 group-data-[open]:block" />
                </DisclosureButton>
              </div>
              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex shrink-0 items-center">
                  <button type="button" onClick={() => onNavigate('landing')} className="cursor-pointer">
                    <img
                      alt="DriftCal"
                      src="/driftcal_256_white.png"
                      className="h-8 w-auto"
                    />
                  </button>
                </div>
                <div className="hidden sm:ml-6 sm:block">
                  <div className="flex space-x-4">
                    {navigation.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        onClick={(e) => {
                          if (item.external) {
                            // Allow default behavior for external links
                            return;
                          }
                          e.preventDefault();
                          item.onClick();
                        }}
                        target={item.external ? '_blank' : undefined}
                        rel={item.external ? 'noopener noreferrer' : undefined}
                        className={classNames(
                          item.external 
                            ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-700' 
                            : item.current 
                              ? 'bg-gray-900 text-white' 
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                          'rounded-md px-3 py-2 text-sm font-medium'
                        )}
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-3 sm:gap-4">
                {/* Country Indicator */}
                {selectedCountry && (
                  countries.length === 1 ? (
                    // Single country - just show the flag and name
                    <div className="flex items-center gap-2 text-gray-300">
                      <img 
                        src={selectedCountry.FlagURL} 
                        alt={selectedCountry.CountryName}
                        className="w-12 object-cover"
                      />
                      
                    </div>
                  ) : (
                    // Multiple countries - show dropdown
                    <Menu as="div" className="relative">
                      <Menu.Button className="flex items-center gap-2 text-gray-300 hover:text-white">
                        <img 
                          src={selectedCountry.FlagURL} 
                          alt={selectedCountry.CountryName}
                          className="w-12 object-cover"
                        />
                        
                      </Menu.Button>
                      <Menu.Items 
                        className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg"
                        style={{ 
                          transform: 'translateZ(0)',  // Force GPU acceleration
                          willChange: 'transform, opacity' 
                        }}
                      >
                        <div className="py-1">
                          {countries.map((country) => (
                            <Menu.Item key={country.CountryID}>
                              {({ active }) => (
                                <button
                                  onClick={() => setSelectedCountry(country)}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 transition-colors"
                                  style={{
                                    backgroundColor: active ? '#f3f4f6' : 'transparent'
                                  }}
                                >
                                  <img 
                                    src={country.FlagURL} 
                                    alt={country.CountryName}
                                    className="w-12 object-cover"
                                    loading="lazy"
                                  />
                                  {country.CountryName}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Menu>
                  )
                )}

                {/* User Icon/Initials */}
                {user ? (
                  <div 
                    className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-600 text-white font-bold cursor-pointer"
                    onClick={onOpenDashboard}
                  >
                    {initials}
                  </div>
                ) : (
                  <button
                    type="button"
                    aria-label="Sign in"
                    onClick={() => setIsLoginOpen(true)}
                    className="text-gray-300 hover:text-white"
                  >
                    <UserCircleIcon className="h-8 w-8" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <Transition
            show={open}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-2"
          >
            <DisclosurePanel className="sm:hidden">
              <div className="space-y-1 px-2 pb-3 pt-2">
                {navigation.map((item, index) => (
                  <DisclosureButton
                    key={item.name}
                    as="a"
                    href={item.href}
                    onClick={(e) => {
                      if (item.external) {
                        // Allow default behavior for external links
                        closeMenu();
                        return;
                      }
                      e.preventDefault();
                      item.onClick();
                      closeMenu();
                    }}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                    className={classNames(
                      item.external 
                        ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-700' 
                        : item.current 
                          ? 'bg-gray-900 text-white' 
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                      'block rounded-md px-3 py-2 text-base font-medium transition-all duration-200',
                    )}
                    style={{
                      animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
                    }}
                  >
                    {item.name}
                  </DisclosureButton>
                ))}
              </div>
            </DisclosurePanel>
          </Transition>

          {/* Login Panel */}
          <LoginPanel 
            isOpen={isLoginOpen} 
            onClose={() => setIsLoginOpen(false)}
            user={user}
            onLogin={(userData) => setUser(userData)}
            onLogout={handleLogout}
            onOpenDashboard={onOpenDashboard}
          />
        </>
      )}
    </Disclosure>
  )
}
